import React, { useEffect, useMemo, useRef, useState } from 'react';
import styled from 'astroturf/react';
import { currentUser } from 'js/utils/user';
import { TextButton } from 'js/design_system/TextButton';
import Divider from 'js/design_system/Divider';
import Feed from './Feed';
import { FeedContext } from './contexts';
import { useFeedConfig, useNews, useUsers } from './Feed/hooks';
import * as api from './api';
import { PostPrompt as PostPromptBase } from './PostPrompt';
import ProfileEmptyState from './ProfileEmptyState';
import CollapsedActivity from './CollapsedActivity';
import { NewsPreview } from './shared/types';
import { ReactQueryProvider } from 'js/social/state/ReactQueryProvider';
import { ExploreConversations } from 'js/social/ExploreConversations';
import { FeedFilters } from 'js/social/FeedFilters';
import BackButton from 'js/social/shared/BackButton';
import { SnackbarProvider } from 'js/social/state/SnackbarProvider';
import { ProfileBulkFollowModal } from 'js/social/shared/big_cards/ProfileBulkFollowModal';
import _Container from "ProfileFeed-Container.module.scss!=!astroturf/inline-loader?style=1!/var/lib/jenkins-code/academia-app/app/assets/javascripts/social/ProfileFeed.tsx?styleId=Container";
import _PostPromptExpanded from "ProfileFeed-PostPromptExpanded.module.scss!=!astroturf/inline-loader?style=1!/var/lib/jenkins-code/academia-app/app/assets/javascripts/social/ProfileFeed.tsx?styleId=PostPromptExpanded";
import _PostPromptCollapsed from "ProfileFeed-PostPromptCollapsed.module.scss!=!astroturf/inline-loader?style=1!/var/lib/jenkins-code/academia-app/app/assets/javascripts/social/ProfileFeed.tsx?styleId=PostPromptCollapsed";
import _ExpandedActivity from "ProfileFeed-ExpandedActivity.module.scss!=!astroturf/inline-loader?style=1!/var/lib/jenkins-code/academia-app/app/assets/javascripts/social/ProfileFeed.tsx?styleId=ExpandedActivity";
import _ButtonContainer from "ProfileFeed-ButtonContainer.module.scss!=!astroturf/inline-loader?style=1!/var/lib/jenkins-code/academia-app/app/assets/javascripts/social/ProfileFeed.tsx?styleId=ButtonContainer";
import _ExpandCtaButton from "ProfileFeed-ExpandCtaButton.module.scss!=!astroturf/inline-loader?style=1!/var/lib/jenkins-code/academia-app/app/assets/javascripts/social/ProfileFeed.tsx?styleId=ExpandCtaButton";


const PAGE = 'profile';

const Container = /*#__PURE__*/styled("div", null, {
  displayName: "Container",
  styles: _Container
});

const PostPromptExpanded = /*#__PURE__*/styled(PostPromptBase, null, {
  displayName: "PostPromptExpanded",
  styles: _PostPromptExpanded
});

const PostPromptCollapsed = /*#__PURE__*/styled(PostPromptBase, null, {
  displayName: "PostPromptCollapsed",
  styles: _PostPromptCollapsed
});

const ExpandedActivity = /*#__PURE__*/styled("div", null, {
  displayName: "ExpandedActivity",
  styles: _ExpandedActivity
});

const ButtonContainer = /*#__PURE__*/styled("div", null, {
  displayName: "ButtonContainer",
  styles: _ButtonContainer
});

const ExpandCtaButton = /*#__PURE__*/styled(TextButton, null, {
  displayName: "ExpandCtaButton",
  styles: _ExpandCtaButton
});

type Props = {
  currentUserId: number;
  viewedUserId: number;
  viewedUserDisplayName: string;
  hasActivity: boolean;
  preview?: NewsPreview;
  filters: string[];
  onsitePodcastPromotion?: boolean;
};

export const ProfileFeed: React.FC<Props> = ({
  currentUserId,
  viewedUserId,
  viewedUserDisplayName,
  hasActivity,
  preview,
  filters,
  onsitePodcastPromotion = false,
}) => {
  const [filter, setFilter] = useState<string>();
  const [showFeed, setShowFeed] = useState(false);

  const getPage = useMemo(
    () => api.getProfileFeedPage(viewedUserId, filter),
    [viewedUserId, filter],
  );

  const feedConfig = useFeedConfig(PAGE);
  const userActions = useUsers(preview?.users);
  const newsActions = useNews({
    page: feedConfig.page,
    getPage,
    setMoreUsers: userActions.setMoreUsers,
    setFeedConfig: feedConfig.setFeedConfig,
  });
  const worksContainerRef = useRef<HTMLElement | null>(null);
  const isOwner = viewedUserId === currentUserId;

  useEffect(() => {
    // The element with 'social-redesign-work-container' id is implemented
    // in backbone, and is very old.
    // The way this component interacts with it is by showing/hiding it
    // when we want to hide/show the profile feed
    worksContainerRef.current = document.getElementById(
      'social-redesign-work-container',
    );
  }, []);

  useEffect(() => {
    if (!worksContainerRef.current) return;
    worksContainerRef.current.style.display = showFeed ? 'none' : '';
  }, [showFeed]);

  if (currentUser == null) {
    return null;
  }

  return (
    <ReactQueryProvider>
      <SnackbarProvider>
        <FeedContext.Provider
          value={{
            feedConfig,
            userActions,
            newsActions,
            useReporting: true,
            recordViews: false,
          }}
        >
          <Container>
            <ProfileBulkFollowModal
              viewedUserDisplayName={viewedUserDisplayName}
              userId={viewedUserId}
            />

            {showFeed ? (
              <>
                <div>
                  <BackButton onClick={() => setShowFeed(false)}>
                    Back
                  </BackButton>
                  <FeedFilters
                    filter={filter}
                    filters={filters}
                    onFilterChange={setFilter}
                  />
                </div>

                <ExpandedActivity>
                  {isOwner && (
                    <PostPromptExpanded
                      fullName={currentUser.display_name || ''}
                      photoUrl={currentUser.photo.replace('s65', 's200')}
                      profileUrl={currentUser.url || ''}
                    />
                  )}

                  <Feed
                    filter={filter}
                    emptyState={
                      <ProfileEmptyState
                        isOwner={isOwner}
                        username={viewedUserDisplayName}
                      />
                    }
                    showReasons={filter === undefined}
                    {...newsActions}
                    {...userActions}
                  />
                </ExpandedActivity>
              </>
            ) : hasActivity || isOwner ? (
              <>
                <CollapsedActivity
                  isOwner={isOwner}
                  displayName={viewedUserDisplayName}
                  preview={preview}
                  onsitePodcastPromotion={onsitePodcastPromotion}
                >
                  {isOwner && (
                    <>
                      <PostPromptCollapsed
                        fullName={currentUser.display_name || ''}
                        photoUrl={currentUser.photo.replace('s65', 's200')}
                        profileUrl={currentUser.url || ''}
                      />
                      {preview && <Divider.s8 />}
                    </>
                  )}
                </CollapsedActivity>

                {hasActivity && (
                  <ButtonContainer>
                    <ExpandCtaButton inline onClick={() => setShowFeed(true)}>
                      Show all activity
                    </ExpandCtaButton>
                  </ButtonContainer>
                )}
              </>
            ) : (
              <ExploreConversations displayName={viewedUserDisplayName} />
            )}
          </Container>
        </FeedContext.Provider>
      </SnackbarProvider>
    </ReactQueryProvider>
  );
};
